import { Component, ErrorInfo, ReactNode } from "react";
import { reportError } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Top-level safety net. If anything in the React tree throws during
 * render/mount, we surface the message instead of leaving a blank
 * <div id="root">. Especially important on mobile where users have no
 * devtools to see the underlying error.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    // audit-console-warnings: allow
    console.error("[ErrorBoundary]", error, info); // eslint-disable-line no-console
    reportError(error, { componentStack: info.componentStack });
  }

  render() {
    if (!this.state.error) return this.props.children;

    const isDev = import.meta.env.DEV;
    return (
      <div
        role="alert"
        style={{
          minHeight: "100dvh",
          padding: "2rem 1.25rem",
          fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif",
          color: "#1a1a1a",
          background: "#faf8f5",
          boxSizing: "border-box",
        }}
      >
        <h1 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>
          Something went wrong loading this page.
        </h1>
        <p style={{ fontSize: "0.95rem", marginBottom: "1rem" }}>
          Please refresh. If the problem persists, try clearing your browser
          cache or visit{" "}
          <a href="https://www.yohq.org" style={{ color: "#8b5a2b" }}>
            yohq.org
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "0.6rem 1rem",
            border: "1px solid #8b5a2b",
            background: "#8b5a2b",
            color: "white",
            cursor: "pointer",
          }}
        >
          Reload
        </button>
        {isDev && (
          <pre
            style={{
              marginTop: "1.5rem",
              padding: "1rem",
              background: "#1a1a1a",
              color: "#f8c8a0",
              fontSize: "0.75rem",
              overflow: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {String(this.state.error?.stack || this.state.error?.message)}
            {this.state.info?.componentStack}
          </pre>
        )}
      </div>
    );
  }
}